/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import Imgix from 'js/components/Imgix';

import { Button, Grid, breakpoints } from '@coursera/cds-core';
import type { PageConfigComponent as PageConfigComponentTrackingData } from '@coursera/event-pulse-types';
import { type EventData, useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import SectionHeadingComponent from 'bundles/browse-common/components/SectionHeadingComponent';
import type { PartnerData } from 'bundles/browse-common/utils/experimentUtils';

import _t from 'i18n!nls/browse-common';

type PartnerCardProps = {
  partner: PartnerData;
  partnerIndex: number;
  loading?: 'eager' | 'lazy';
  pageConfigComponentTrackingData?: PageConfigComponentTrackingData;
};

type PartnerListProps = {
  partners: PartnerData[];
  loading?: 'eager' | 'lazy';
  pageConfigComponentTrackingData?: PageConfigComponentTrackingData;
};

const PARTNER_LOGO_WIDTH = 147;
const PARTNER_LOGO_HEIGHT = 52;

const styles = {
  componentContainer: css`
    ${breakpoints.up('md')} {
      padding: var(--cds-spacing-200) 0;
    }
    ${breakpoints.down('md')} {
      padding: var(--cds-spacing-150) 0;
    }
  `,
  partnerListContainer: css`
    list-style: none;
    padding-left: 0;
    margin: 0;
    align-items: center;
    row-gap: var(--cds-spacing-200);
    ${breakpoints.up('lg')} {
      justify-content: space-between;
    }
  `,

  partnerCard: css`
    padding: 0 !important;
    margin: 0 !important;
    border-radius: var(--cds-border-radius-100);
    width: 100%;
    display: flex;
    justify-content: center;

    :hover {
      background-color: var(--cds-color-lighten-0);
    }

    :focus {
      background-color: var(--cds-color-lighten-0);
    }
  `,
  partnerTitle: css`
    margin-bottom: var(--cds-spacing-150);
  `,
  partnerSvg: css`
    width: ${PARTNER_LOGO_WIDTH}px;
    height: ${PARTNER_LOGO_HEIGHT}px;
    object-fit: contain;
    object-position: center;
    ${breakpoints.down('md')} {
      max-width: ${PARTNER_LOGO_WIDTH}px;
      max-height: ${PARTNER_LOGO_HEIGHT}px;
      width: auto;
      height: auto;
    }
  `,
};

const PartnerCard = ({ partner, partnerIndex, loading, pageConfigComponentTrackingData }: PartnerCardProps) => {
  const track = useTracker();

  const partnerCardTrackingData: EventData<'view_partner_card'> = {
    partnerName: partner.partnerName,
    partnerCardIndex: partnerIndex,
    ...(pageConfigComponentTrackingData && { pageConfigComponent: pageConfigComponentTrackingData }),
  };

  const visibilityRef = useVisibilityTracker<HTMLDivElement>('view_partner_card', partnerCardTrackingData, [], {
    fullyVisible: true,
  });

  const onClickToTrack = () => {
    track('click_partner_card', partnerCardTrackingData);
  };

  return (
    <div ref={visibilityRef}>
      <Button
        component="a"
        href={`/partners/${partner.relationSlug}`}
        css={styles.partnerCard}
        onClick={onClickToTrack}
        variant="ghost"
        aria-label={partner.partnerName}
      >
        {/* Some svg logos weren't being rasterized properly by Imgix */}
        {partner.image.url.endsWith('.svg') ? (
          <img src={partner.image.url} alt={partner.imageAltText} loading={loading} css={styles.partnerSvg} />
        ) : (
          <Imgix
            src={partner.image.url}
            alt={partner.imageAltText}
            loading={loading}
            maxWidth={PARTNER_LOGO_WIDTH}
            maxHeight={PARTNER_LOGO_HEIGHT}
          />
        )}
      </Button>
    </div>
  );
};

const PartnerList = (props: PartnerListProps) => {
  const { partners, loading = 'lazy', pageConfigComponentTrackingData } = props;
  return (
    <div css={styles.componentContainer} data-testid="leading-partners">
      <SectionHeadingComponent>{_t('Leading partners')}</SectionHeadingComponent>
      <Grid container component="ul" css={styles.partnerListContainer} spacing={{ lg: 0, md: 12, sm: 12, xs: 12 }}>
        {partners.map((partner, index) => {
          return (
            <Grid component="li" item xs={6} sm={3} md={3} lg={1} key={partner.relationSlug}>
              <PartnerCard
                partnerIndex={index}
                partner={partner}
                loading={loading}
                pageConfigComponentTrackingData={pageConfigComponentTrackingData}
              />
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
};

export default PartnerList;
